import React, { useState } from 'react';
import { X, Map as MapIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import MapLayerBuilder from './MapLayerBuilder';
import MapArtefactGallery from './MapArtefactGallery';

const MapLayerBuilderModal = ({ isOpen, onClose, workspaceId, chatId, availableDatasets = [], onSave, onPreview }) => {
  const [activeTab, setActiveTab] = useState('new'); // 'new' | 'existing'
  const [selectedArtefact, setSelectedArtefact] = useState(null);

  if (!isOpen) return null;

  const handleSelectArtefact = (artefact) => {
    // Directly add the layer to the map
    const stableId = `art-${artefact.id}`;
    onSave(artefact.spec, artefact.title || 'Saved Map Layer', artefact.config || {}, stableId);
    onClose();
    // Reset state
    setSelectedArtefact(null);
    setActiveTab('new');
  };

  const handleSave = (payload, desc, config, layerId) => {
    onSave(payload, desc, config, layerId);
    onClose();
    // Reset state
    setSelectedArtefact(null);
    setActiveTab('new');
  };

  const handleCancel = () => {
    onClose();
    // Reset state
    setSelectedArtefact(null);
    setActiveTab('new');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={handleCancel} />
      <div className="relative bg-white dark:bg-gray-950 w-full max-w-2xl rounded-[40px] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="p-8 pb-0 border-b border-gray-50 dark:border-gray-900/50 shrink-0">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-2xl">
                <MapIcon size={28} />
              </div>
              <div>
                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tighter leading-none">
                  {activeTab === 'new' ? 'New Map Layer' : 'Existing Map Layers'}
                </h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                  {activeTab === 'new' ? 'Configure your spatial data visualization' : 'Select a saved map layer to reuse'}
                </p>
              </div>
            </div>
            <button onClick={handleCancel} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              <X size={24} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-8">
            <button
              onClick={() => setActiveTab('new')}
              className={cn(
                "pb-4 text-xs font-black uppercase tracking-[0.2em] transition-all relative",
                activeTab === 'new' ? "text-emerald-600" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              )}
            >
              New Layer
              {activeTab === 'new' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-emerald-600 rounded-full" />}
            </button>
            <button
              onClick={() => setActiveTab('existing')}
              className={cn(
                "pb-4 text-xs font-black uppercase tracking-[0.2em] transition-all relative",
                activeTab === 'existing' ? "text-emerald-600" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              )}
            >
              Existing Layers
              {activeTab === 'existing' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-emerald-600 rounded-full" />}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar relative">
          {activeTab === 'new' ? (
            <MapLayerBuilder
              initialState={selectedArtefact ? {
                dataset: selectedArtefact.spec?.dataset,
                spatial: selectedArtefact.spec?.spatial,
                time: selectedArtefact.spec?.time,
                value: selectedArtefact.spec?.value,
                filters: selectedArtefact.spec?.filters || [],
                config: selectedArtefact.config || {},
                name: selectedArtefact.title,
                layerId: selectedArtefact.id
              } : null}
              workspaceId={workspaceId}
              chatId={chatId}
              availableDatasets={availableDatasets}
              onSave={handleSave}
              onPreview={onPreview}
              onCancel={handleCancel}
            />
          ) : (
            <MapArtefactGallery
              workspaceId={workspaceId}
              onSelectArtefact={handleSelectArtefact}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MapLayerBuilderModal;
